import React, { useState, useEffect } from "react";
import { createPageUrl } from "@/utils";
import { Link } from "react-router-dom";
import { db } from "@/api/db";
import { BookOpen, Award, Star, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export default function MyProgress() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [progress, setProgress] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [trackStats, setTrackStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const u = await db.auth.me();
    setUser(u);
    const [profiles, prog, certs] = await Promise.all([
      db.entities.UserProfile.filter({ user_email: u.email }, "-created_date", 1),
      db.entities.UserProgress.filter({ user_email: u.email }, "-created_date"),
      db.entities.Certificate.filter({ user_email: u.email }),
    ]);
    setProfile(profiles[0] || null);
    setProgress(prog);
    setCertificates(certs);

    // Group by track
    const trackMap = {};
    prog.forEach(p => {
      if (!p.track_id) return;
      if (!trackMap[p.track_id]) trackMap[p.track_id] = { track_id: p.track_id, count: 0 };
      trackMap[p.track_id].count++;
    });

    const statArr = await Promise.all(Object.values(trackMap).map(async t => {
      const tracks = await db.entities.CareerTrack.filter({ id: t.track_id });
      const totalLessons = await db.entities.Lesson.filter({ track_id: t.track_id, is_published: true });
      return { ...t, track: tracks[0], total: totalLessons.length };
    }));
    setTrackStats(statArr);
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalPts = profile?.portus_score || 0;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Meu Progresso</h1>
        <p className="text-gray-500 mt-1">Acompanhe sua evolução na Portüs Academy.</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <Star className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{totalPts}</p>
            <p className="text-gray-500 text-sm">Portüs Score</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <BookOpen className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{progress.length}</p>
            <p className="text-gray-500 text-sm">Aulas concluídas</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <Award className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{certificates.length}</p>
            <p className="text-gray-500 text-sm">Certificados</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{trackStats.length}</p>
            <p className="text-gray-500 text-sm">Trilhas iniciadas</p>
          </CardContent>
        </Card>
      </div>

      {/* Track progress */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Progresso por Trilha</h2>
        {trackStats.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-10 text-center text-gray-400">
              <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p>Nenhuma trilha iniciada ainda.</p>
              <Link to={createPageUrl("Tracks")} className="text-emerald-600 text-sm underline mt-2 block">Explorar trilhas</Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {trackStats.map(ts => {
              const pct = ts.total > 0 ? Math.round((ts.count / ts.total) * 100) : 0;
              return (
                <Card key={ts.track_id} className="border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-gray-900">{ts.track?.title || "Trilha"}</p>
                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">{pct}%</Badge>
                    </div>
                    <Progress value={pct} className="h-2 mb-1" />
                    <p className="text-xs text-gray-400">{ts.count} de {ts.total} aulas concluídas</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Certificates */}
      {certificates.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Certificados Obtidos</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {certificates.map(cert => (
              <Card key={cert.id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <Award className="w-8 h-8 text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{cert.track_title}</p>
                    <p className="text-gray-400 text-xs">{cert.level_title} · {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString("pt-BR") : ""}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}